import React, { useEffect, useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  BarChartOutlined,
  PieChartOutlined,
  MessageOutlined,
  AssessmentOutlined,
} from '@mui/icons-material';
import { ChartRenderer } from './charts/ChartRenderer';
import { CommentsSection } from './CommentsSection';
import { QuestionReportData, QUESTION_TYPE_LABELS } from '../types';
import { REPORT_ANALYTICS_EVENTS } from '../../../utils/trackingEvents';
import { usePostHog } from '../../../hooks/usePostHog';

interface QuestionReportCardProps {
  questionReport: QuestionReportData;
  isHighlighted?: boolean;
  showComments?: boolean;
  compact?: boolean;
  chartSize?: 'small' | 'medium' | 'large';
  maxComments?: number;
  className?: string;
}

const getQuestionTitle = (questionReport: QuestionReportData): string => {
  return (
    questionReport.questionTitle?.en ||
    questionReport.questionTitle?.no ||
    Object.values(questionReport.questionTitle)[0] ||
    'Untitled Question'
  );
};

const getQuestionTypeIcon = (questionType: string) => {
  switch (questionType) {
    case 'YES_NO':
    case 'SINGLE_SELECT':
      return <PieChartOutlined />;
    case 'MULTI_SELECT':
    case 'RANGE':
      return <BarChartOutlined />;
    case 'TEXT':
    case 'NUMBER':
      return <MessageOutlined />;
    default:
      return <AssessmentOutlined />;
  }
};

// Removed StatisticItem component as statistics are no longer displayed

export const QuestionReportCard: React.FC<QuestionReportCardProps> = ({
  questionReport,
  isHighlighted = false,
  showComments = true,
  compact = false,
  chartSize = 'medium',
  maxComments,
  className,
}) => {
  const { captureEvent } = usePostHog();
  const { questionType, chartConfig, includedComments, rawDataSummary, order } = questionReport;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [viewStartTime] = useState<number>(Date.now());
  const [hasBeenViewed, setHasBeenViewed] = useState<boolean>(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Track question view when it comes into viewport
  useEffect(() => {
    const cardElement = cardRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasBeenViewed) {
            setHasBeenViewed(true);

            const viewDuration = (Date.now() - viewStartTime) / 1000;
            captureEvent(REPORT_ANALYTICS_EVENTS.QUESTION_VIEW_DURATION, {
              question_id: questionReport.questionId,
              question_type: questionType,
              question_order: order,
              chart_type: chartConfig?.chartType || 'none',
              has_comments: includedComments.length > 0,
              comment_count: includedComments.length,
              view_duration_seconds: viewDuration,
              response_count:
                chartConfig?.data?.datasets?.[0]?.data?.reduce((sum, val) => sum + val, 0) || 0,
            });
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% of the card is visible
    );

    if (cardElement) {
      observer.observe(cardElement);
    }

    return () => {
      if (cardElement) {
        observer.unobserve(cardElement);
      }
    };
  }, [
    hasBeenViewed,
    viewStartTime,
    questionReport,
    questionType,
    chartConfig,
    includedComments,
    order,
    captureEvent,
  ]);

  const questionTitle = getQuestionTitle(questionReport);
  const questionTypeLabel =
    QUESTION_TYPE_LABELS[questionType as keyof typeof QUESTION_TYPE_LABELS] || questionType;

  // Determine if this question should be highlighted
  const shouldHighlight = isHighlighted;

  // Get highlight color
  const highlightColor = '#1976d2';

  return (
    <Card
      ref={cardRef}
      className={className}
      elevation={0}
      sx={(theme) => ({
        mb: compact ? 2 : 3,
        border: shouldHighlight ? `2px solid ${highlightColor}` : '1px solid',
        borderColor: shouldHighlight ? highlightColor : 'divider',
        backgroundColor: shouldHighlight
          ? alpha(highlightColor, 0.02)
          : theme.palette.mode === 'light'
            ? '#ffffff'
            : theme.palette.background.paper,
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.012), 0px 2px 8px rgba(0, 0, 0, 0.012)',
        transition: 'all 0.3s ease-in-out',
        transform: shouldHighlight ? 'scale(1.01)' : 'none',
      })}
    >
      <CardContent sx={{ p: isMobile ? 2 : compact ? 2 : 3 }}>
        {/* Question Header */}
        <Box
          display="flex"
          alignItems={isMobile ? 'flex-start' : 'center'}
          justifyContent="space-between"
          mb={isMobile ? 2 : 3}
          flexDirection={isMobile ? 'column' : 'row'}
          gap={isMobile ? 1 : 0}
        >
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1} mb={1} flexWrap="wrap">
              <Chip
                label={`Q${order}`}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: shouldHighlight ? alpha(highlightColor, 0.1) : 'transparent',
                  borderColor: shouldHighlight ? highlightColor : 'primary.main',
                  color: shouldHighlight ? highlightColor : 'primary.main',
                }}
              />
              <Chip
                icon={getQuestionTypeIcon(questionType)}
                label={questionTypeLabel}
                size="small"
                variant="outlined"
                color="secondary"
              />
            </Box>

            <Typography
              variant={isMobile ? 'subtitle1' : 'h6'}
              component="h3"
              gutterBottom
              sx={{
                color: shouldHighlight ? highlightColor : 'text.primary',
                fontWeight: shouldHighlight ? 'bold' : 'medium',
                fontSize: isMobile ? '1rem' : undefined,
              }}
            >
              {questionTitle}
            </Typography>
          </Box>
        </Box>

        {/* Main Content - Chart and Comments */}
        <Box>
          {/* Chart Section */}
          {(chartConfig || rawDataSummary) && (
            <Box mb={isMobile ? 2 : 3}>
              <ChartRenderer
                questionReport={questionReport}
                size={isMobile ? 'small' : chartSize}
                showTitle={false}
              />
            </Box>
          )}

          {/* Comments Section */}
          {showComments && includedComments.length > 0 && (
            <Box>
              <CommentsSection
                questionReport={questionReport}
                maxComments={maxComments}
                compact={compact}
              />
            </Box>
          )}
        </Box>

        {/* Empty state for questions without data */}
        {!chartConfig && !rawDataSummary && includedComments.length === 0 && (
          <Box textAlign="center" py={4}>
            <AssessmentOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No data available for this question.
            </Typography>
          </Box>
        )}

        {/* Highlight indicator */}
        {shouldHighlight && (
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height={4}
            sx={{
              background: `linear-gradient(90deg, ${highlightColor}, ${alpha(highlightColor, 0.6)})`,
              borderRadius: '4px 4px 0 0',
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};
